import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection';
import {AccountStatusFilter} from 'src/components/filters';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {AccountSortField} from 'src/openapi/admin/models/AccountSortField';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {SortHeaderFilter} from '@pb/kit-component-filters';

import {COLUMNS, HEADERS, SELECTION_KEY} from '../../constants';
import {
    accountSearchResultStateActions,
    accountSearchResultStateSelector,
} from '../../slices/account-search-result-state.slice';

import type {SortDirection} from 'src/openapi/admin/models/SortDirection';


export const AccountStatusHeader = () => {
    const dispatch = useDispatch();
    const cacheKeys = useSelector(accountSearchResultStateSelector);

    const [accountStatus, setAccountStatus] = useState(
        cacheKeys.filters.accountStatus as string[] | undefined,
    );

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            setAccountStatus(current as string[] | undefined);
        }
    }, cacheKeys.filters.accountStatus);

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setSortCriteria({
            direction: direction as SortDirection,
            field: AccountSortField.ACCOUNT_STATUS,
        }));
    }, [dispatch]);

    const applyFilter = useCallback(() => {
        const {accountStatus: _prevStatuses, ...otherFilters} = cacheKeys.filters;

        dispatch(clearSelection(SELECTION_KEY));
        if (accountStatus?.length) {
            dispatch(accountSearchResultStateActions.setFilters({
                ...otherFilters,
                accountStatus,
            }));
        } else {
            dispatch(accountSearchResultStateActions.setFilters(otherFilters));
        }
    }, [accountStatus, dispatch, cacheKeys.filters]);

    const clearFilter = useCallback(() => {
        const {accountStatus: _prevStatues, ...otherFilters} = cacheKeys.filters;

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setFilters(otherFilters));
    }, [dispatch, cacheKeys.filters]);

    const cancelFilter = useCallback(() => {
        setAccountStatus(cacheKeys.filters.accountStatus as string[] | undefined);
    }, [cacheKeys.filters.accountStatus]);

    return (
        <SortHeaderFilter
            filterActive={!!cacheKeys.filters?.accountStatus}
            sortable={true}
            caption={HEADERS[COLUMNS.ACCOUNT_STATUS]}
            direction={cacheKeys.sortCriteria.field === AccountSortField.ACCOUNT_STATUS
                ? cacheKeys.sortCriteria.direction
                : undefined}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <AccountStatusFilter selectedStatuses={accountStatus} onSelect={setAccountStatus}/>
        </SortHeaderFilter>
    );
};
